import { useLazyGetPlaybookQuery } from '@carboncopyco/data';
import { CloseOutlineIcon, PlayIcon } from '@carboncopyco/icons';
import type { IDemo, Playbook } from '@carboncopyco/types';
import {
  type IGlobalTab,
  TabGroupName,
  TabType,
} from '@carboncopyco/types/desktop';
import { Loader as AppLoader } from '@carboncopyco/ui';
import { useAnalytics } from '@carboncopyco/utils';
import classNames from 'classnames';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { MemoryRouter, Route, Routes } from 'react-router-dom';
import { AnalyticsEvents } from '../../shared/constants/analytics.events';
import { AppEventsEnum } from '../../shared/enums/AppEventsEnum';
import {
  isArchivedDemoOrPresentation,
  isTabOpenFromEditor,
} from '../../shared/globalTabUtil';
import { useEditorPageInfo } from '../components/EditorContainer/hooks';
import GiveADemoModalExtended from '../components/GiveADemoModalExtended';
import InnerViewArea from '../components/InnerViewArea';
import PresentAndPreviewToolbar from '../components/PresentAndPreviewToolbar';
import { useCustomNavigate } from '../hooks/useCustomNavigate';
import useEditorGroupTab from '../hooks/useEditorGroupTab';
import { useFocusAppService } from '../hooks/useFocusAppService';
import { useNavigation } from '../hooks/useNavigation';
import useStartDemo from '../hooks/useStartDemo';
import { HOME_TAB_ID, useTabManager } from '../services/TabManager';
import { isActiveOrAllInnerTabsLoaded } from '../shared/utils';
import { useTabs, useUser } from '../store/context';
import { ResponsiveModeContextProvider } from '../store/context/ResponsiveModeContext';
import BrowserWindowErrorScreen from './BrowserWindowErrorScreen';
import { previewFrameBorder } from './constants/previewFrameBorder';

interface Props {
  tab: IGlobalTab;
  showCtaButton: boolean;
  onGoBack?: () => void;
}

const BrowserWindowNavBar = ({ tab, showCtaButton, onGoBack }: Props) => {
  const demo = tab.payload?.demo as IDemo;
  const [activePlaybook, setActivePlaybook] = useState<Playbook>();
  const [showCreatePresentation, setShowCreatePresentation] = useState(false);
  const [isCreatePresentationLoading, setIsCreatePresentationLoading] =
    useState(true);
  const { links } = useCustomNavigate();
  const { trackEvent } = useAnalytics();
  const { goBack } = useNavigation(tab);
  const {
    updateTabProperty,
    globalTabs,
    closeGroupTabsWithException,
    closeGroupTabs,
  } = useTabManager();
  const pageInfo = useEditorPageInfo(tab);
  const [getPlaybook] = useLazyGetPlaybookQuery();
  const { activeOrganization } = useUser();
  const { setActiveGlobalTabId, setRedirectRoute } = useTabs();
  const isInnerViewLoaded = isActiveOrAllInnerTabsLoaded(tab);
  const { getFirstGroupTabByGroupId, updateEditorTabVisibility } =
    useEditorGroupTab();
  const { onStartDemo } = useStartDemo({
    tab,
    setIsCreatePresentationLoading,
    setShowCreatePresentation,
  });

  const fetchPlaybook = useCallback(async (workspaceId: string) => {
    const playbook = await getPlaybook({
      workspaceId,
      organizationId: String(activeOrganization.id),
      id: tab.payload.play.playbookId,
    }).unwrap();
    setActivePlaybook(playbook);
  }, []);

  useEffect(() => {
    tab?.workspaceId &&
      tab?.payload?.play?.playbookId &&
      fetchPlaybook(tab?.workspaceId);
  }, [tab?.payload?.play?.playbookId, tab?.workspaceId]);

  useEffect(() => {
    if (isInnerViewLoaded && isCreatePresentationLoading) {
      setIsCreatePresentationLoading(false);
    }
  }, [isInnerViewLoaded]);

  const isPreviewPlaybookUrlAsset = !!tab?.group?.name?.includes(
    TabGroupName.PlaybookPreview
  );

  const handlePresentationUrlVisitTracking = () => {
    const presentationTabs = [
      TabGroupName.PlaybookPresentation,
      TabGroupName.DemoPresent,
    ];
    const isPartOfPresentationTabGroup =
      tab.group?.name && presentationTabs.includes(tab.group.name);
    const isNotPlaybookUrlAsset =
      tab.group && !tab.group.mergingOptions?.allowed;
    const canTrackUrl =
      tab?.url && isPartOfPresentationTabGroup && isNotPlaybookUrlAsset;

    if (!canTrackUrl) return;

    const relatedGroupTabs = globalTabs.filter(
      ({ group }) => group?.id === tab.group?.id
    );

    const presentationId = relatedGroupTabs[0]?.payload?.presentation?.id;
    if (!presentationId) return;

    trackEvent(AnalyticsEvents.PRESENTATION_BROWSER_MODE_URL_VISIT, {
      presentationId,
      variables: {
        url: tab?.url,
      },
    });
  };

  useEffect(() => {
    handlePresentationUrlVisitTracking();
  }, [tab?.url]);

  const isArchivedMode = isArchivedDemoOrPresentation(tab) && !!tab?.group?.id;

  const showStartDemo =
    !isArchivedMode &&
    !tab?.payload?.isOpenedFromEditor &&
    tab.type !== TabType.DEMO_MOBILE_PREVIEW;

  const onExitPreviewClick = () => {
    const editorTab = tab.group && getFirstGroupTabByGroupId(tab.group.id);
    if (editorTab) {
      updateEditorTabVisibility({ editorTab, show: true });
    }
    // close all group tab that are open from editor switch to editor tab if there are none tabs close active
    if (isArchivedMode) {
      closeGroupTabs(tab);
    } else closeGroupTabsWithException(tab, isTabOpenFromEditor, editorTab);
  };

  const ctaButton = showStartDemo
    ? {
        title: 'Start demo',
        onClick: onStartDemo,
        icon: <PlayIcon />,
      }
    : {
        title: 'Exit preview',
        onClick: onExitPreviewClick,
        icon: <CloseOutlineIcon />,
      };

  const entity = activePlaybook ?? demo;

  return (
    <>
      <PresentAndPreviewToolbar
        hideNavbar={tab?.isMobileTab}
        tab={tab}
        speakerPannel={{
          isVisible: showStartDemo || isPreviewPlaybookUrlAsset,
        }}
        onGoBack={() => {
          goBack();
          onGoBack?.();
        }}
        onSubmitUrl={(url) => {
          window.dsDesktop.navigation.browserModeSubmitUrl(
            url,
            tab.innerTabs[0].id
          );
          updateTabProperty(tab, 'url', url);
        }}
        urlEditable
        pageInfo={pageInfo}
        ctaButton={showCtaButton ? ctaButton : undefined}
      />
      {showCreatePresentation && entity && (
        <GiveADemoModalExtended
          open
          workspaceId={entity.workspaceId}
          entity={entity}
          onPresent={() => {
            setShowCreatePresentation(false);
            setIsCreatePresentationLoading(false);
          }}
          onSave={() => {
            const redirectUrl = activePlaybook
              ? links.getPlaybookPresentationTabLink(
                  activePlaybook.id,
                  demo.workspaceId
                )
              : links.getPresentationTabLink(demo.id, demo.workspaceId);

            setActiveGlobalTabId(HOME_TAB_ID);
            setRedirectRoute(redirectUrl);
            setShowCreatePresentation(false);
            setIsCreatePresentationLoading(false);
          }}
          onClose={() => {
            setShowCreatePresentation(false);
            setIsCreatePresentationLoading(false);
          }}
        />
      )}
    </>
  );
};

const BrowserWindow = ({ tab }: { tab: IGlobalTab }) => {
  const isInnerViewLoaded = isActiveOrAllInnerTabsLoaded(tab);
  const { goBack } = useNavigation(tab);
  const { triggerFocus } = useFocusAppService();
  const { setGlobalTabs, activeGlobalTabId, globalTabs } = useTabs();
  const { getActiveGlobalTab } = useTabManager();

  const currentTab = useMemo(
    () => getActiveGlobalTab(),
    [activeGlobalTabId, globalTabs]
  );

  const tabGroupName = tab?.group?.name;
  const showCtaButton =
    tabGroupName === TabGroupName.PlaybookPreview ||
    tabGroupName === TabGroupName.DemoPreview ||
    tabGroupName === TabGroupName.ClonePreview;

  useEffect(() => {
    if (currentTab?.isBrowserUrlValid === false) {
      triggerFocus(true);
    }
  }, [triggerFocus, currentTab?.isBrowserUrlValid]);

  const setGlobalTabBrowserUrlValid = useCallback(
    (tabId: number, isValid: boolean) => {
      setGlobalTabs(
        globalTabs.map((prevTab) =>
          prevTab.id === tabId
            ? { ...prevTab, isBrowserUrlValid: isValid }
            : prevTab
        )
      );
    },
    [setGlobalTabs, globalTabs]
  );

  const handleInvalidUrl = () => {
    setGlobalTabBrowserUrlValid(activeGlobalTabId, false);
    triggerFocus(true);
  };

  useEffect(() => {
    window.dsDesktop?.ipcRenderer.on(
      AppEventsEnum.MAIN_TO_APP_VIEW_BROWSER_MODE_INVALID_URL,
      handleInvalidUrl
    );
    return () => {
      window.dsDesktop?.ipcRenderer.removeListener(
        AppEventsEnum.MAIN_TO_APP_VIEW_BROWSER_MODE_INVALID_URL,
        handleInvalidUrl
      );
    };
  }, [globalTabs]);

  const onGoBack = () => {
    goBack();
    setGlobalTabBrowserUrlValid(tab.id, true);
    triggerFocus(false);
  };

  return (
    <ResponsiveModeContextProvider>
      <div
        className="flex flex-col grow relative"
        data-testid="browser-window"
      >
        <BrowserWindowNavBar
          tab={tab}
          onGoBack={onGoBack}
          showCtaButton={showCtaButton}
        />
        {currentTab?.isBrowserUrlValid === false ? (
          <BrowserWindowErrorScreen onGoBack={onGoBack} />
        ) : (
          <div
            className={classNames('flex flex-col grow relative', {
              [previewFrameBorder]: showCtaButton,
            })}
          >
            <InnerViewArea tab={tab} />
            {!isInnerViewLoaded && <AppLoader show />}
          </div>
        )}
      </div>
    </ResponsiveModeContextProvider>
  );
};

const BrowserWindowScreen = ({ tab }: { tab: IGlobalTab }) => (
  <MemoryRouter>
    <Routes>
      <Route
        path="/*"
        element={<BrowserWindow tab={tab} />}
      />
    </Routes>
  </MemoryRouter>
);

export default BrowserWindowScreen;
